import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { display } from 'styled-system';
import type { DisplayProps, SpaceProps } from 'styled-system';

import googleSvg from 'common/assets/images/google.svg';
import { Flex } from 'common/components/Flex';
import { useLocalization } from 'common/providers/LocaleProvider';
import { captureException } from 'common/utils/error-reporter';
import { useGoogleSignIn } from './hooks/google-sign-in';
import type { PerformLogin } from './hooks/third-party-login';
import ThirdPartyButton from './ThirdPartyButton';

interface GoogleButtonProps extends SpaceProps {
  setCommonError: () => void;
  performLogin: PerformLogin;
  isDisabled?: boolean;
  placement?: string;
}

const GoogleButtonContainer = styled.div<DisplayProps>`
  ${display};
`;

const GoogleButton = ({
  setCommonError,
  performLogin,
  placement,
  isDisabled = false,
  ...spaceProps
}: GoogleButtonProps) => {
  const { r } = useLocalization();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const { isLoading, isInitialized } = useGoogleSignIn({
    ref: googleButtonRef,
    setCommonError,
    performLogin,
    placement,
  });

  const onClick = () => {
    const button =
      document.querySelector('#google-button [id^="gsi_"] [id$="-overlay"]') ||
      document.querySelector('#google-button div[role="button"]');

    if (button) {
      button.dispatchEvent(new MouseEvent('click'));
    } else {
      captureException(new Error('Could not find rendered Google button'));
    }
  };

  return (
    <Flex mt="sp_16" mb="sp_12" width="100%" position="relative" {...spaceProps}>
      <ThirdPartyButton
        label={r.t('h24_social_sign_in_label', { provider: 'Google' })}
        isLoading={isLoading}
        imgSrc={googleSvg}
        onClick={onClick}
        isDisabled={isDisabled || !isInitialized}
      />
      <GoogleButtonContainer
        display="none"
        id="google-button"
        data-testid="google-button"
        ref={googleButtonRef}
      />
    </Flex>
  );
};

export default GoogleButton;
